import { ChangeDetectionStrategy, Component, Inject, Input, OnInit } from '@angular/core';

import { BetslipConfig } from '@frontend/sports/common/client-config-data-access';
import { interpolateDoubleBracesString } from '@frontend/sports/common/core/utils/string';
import { Odds } from '@frontend/sports/odds/feature';
import { Store } from '@ngrx/store';
import { Decimal } from 'decimal.js';
import {
    ILinearBetslipMessagesProviderService,
    LINEAR_BETSLIP_MESSAGES_PROVIDER_INJECTION_TOKEN,
} from 'packages/sports/common/betslip/base/services/betslip-messages-provider.service';
import { IBetslipRootState } from 'packages/sports/common/betslip/base/store/state';
import { BetslipType } from 'packages/sports/common/betslip/core/betslip-type';
import { EpcotConfigService, EpcotModule } from 'packages/sports/web/app/src/common/epcot-config.service';

import { BetslipBetBuilderPick } from '../../../../../core/picks/betslip-bet-builder-pick';
import { BetslipGroupPick } from '../../../../../core/picks/betslip-group-pick';
import { BetslipPick } from '../../../../../core/picks/betslip-pick';
import { ISucceededSlipResult } from '../../../../betplacement/models';
import { BetBuilderPick } from '../../../../linear-bet-builder/models';
import { BetslipResultActions } from '../../../actions';
import { IResultSucceededSlipSummaryViewModel, ISucceededSlipRewardTokenInfo } from '../../../models/result-succeeded-slip.model';
import { IBetslipResultState } from '../../../state';
import LinearResultBaseComponent from './linear-result-base.component';
import { DigitalResultSummaryService } from './linear-result-summary.service';

@Component({
    selector: 'bs-digital-linear-result-bet-builder',
    templateUrl: './linear-result-bet-builder.component.html',
    styleUrls: ['base-linear-result-model.component.scss', './linear-result-bet-builder.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [DigitalResultSummaryService],
    standalone: false,
})
export class LinearResultBetBuilderComponent extends LinearResultBaseComponent implements OnInit {
    @Input() succeededSlips: ISucceededSlipResult[];

    betType = BetslipType.BetBuilder;

    title: string;
    subTitle: string;
    totalOdds?: Odds;
    keepPlacedBetId: string;
    boostedOdds?: Odds;
    summaryViewModels: Record<string, IResultSucceededSlipSummaryViewModel> = {};

    readonly isEpcotEnabled: boolean;
    readonly showOneBetBuilderPerModule: boolean;
    readonly betbuilderComboHeaderSubTitle: string;
    readonly babResultSingleTitle: string;
    readonly babResultTitle: string;

    constructor(
        private resultSummaryService: DigitalResultSummaryService,
        private betslipConfig: BetslipConfig,
        @Inject(LINEAR_BETSLIP_MESSAGES_PROVIDER_INJECTION_TOKEN) messagesProvider: ILinearBetslipMessagesProviderService,
        public store: Store<IBetslipRootState>,
        epcotConfig: EpcotConfigService,
    ) {
        super(store);

        this.messages = messagesProvider.getMessages();
        this.betbuilderComboHeaderSubTitle = this.messages.betslip.common.BetbuilderComboHeaderSubTitle;
        this.babResultSingleTitle = this.messages.linear.BabResultSingleTitle;
        this.babResultTitle = this.messages.linear.BabResultTitle;

        this.showOneBetBuilderPerModule = !this.betslipConfig.isLinearSeparateBetBuilderModuleEnabled;
        this.isEpcotEnabled = epcotConfig.isEnabled(EpcotModule.Betslip);
    }

    ngOnInit() {
        this.title = this.getTitle();
        this.subTitle = this.getSubTitle();

        this.totalOdds = this.showOneBetBuilderPerModule ? this.succeededSlips[0].totalOdds : undefined;
        this.boostedOdds = this.showOneBetBuilderPerModule ? this.succeededSlips[0].boostedOdds : undefined;

        this.summaryViewModels = this.getSummaryViewModels();
        this.keepPlacedBetId = this.getKeepPlaceBetId();
    }

    keepPlaced(resultState: IBetslipResultState): boolean {
        const groupId = this.getGroupId(this.succeededSlips[0]);

        return resultState.keepPlacedBetsInLinearBetBuilder[groupId];
    }

    onKeepPlacedChanged(keepPlacedBetsInLinearBetBuilder: boolean): void {
        this.succeededSlips.forEach((slip) => {
            const groupId = this.getGroupId(slip);

            return this.store.dispatch(
                BetslipResultActions.keepPlacedBetsInLinearBetBuilder({
                    groupId,
                    keepPlacedBetsInLinearBetBuilder,
                }),
            );
        });
    }

    getStake(betNumber: string): number | undefined {
        return this.summaryViewModels[betNumber].stake;
    }

    getPossibleWinningsNet(betNumber: string): Decimal {
        return this.summaryViewModels[betNumber].possibleWinnings?.value;
    }

    getPossibleWinningsGross(betNumber: string): Decimal | undefined {
        return this.summaryViewModels[betNumber].possibleWinningsGross;
    }

    getRewardTokenInfo(betNumber: string): ISucceededSlipRewardTokenInfo | undefined {
        return this.summaryViewModels[betNumber].rewardTokenInfo;
    }

    getBoostedValue(betNumber: string): Decimal | undefined {
        return this.summaryViewModels[betNumber].possibleWinnings?.boostedValue;
    }

    isBetBuilderPick(pick?: BetslipPick): pick is BetBuilderPick {
        return pick instanceof BetslipBetBuilderPick || pick instanceof BetslipGroupPick;
    }

    private getKeepPlaceBetId(): string {
        return this.betType.concat(this.getGroupId(this.succeededSlips[0]));
    }

    private getSummaryViewModels(): Record<string, IResultSucceededSlipSummaryViewModel> {
        return this.succeededSlips.reduce<Record<string, IResultSucceededSlipSummaryViewModel>>(
            (acc, curr) => {
                acc[curr.betNumber] = this.resultSummaryService.getSummaryViewModel(curr, this.betType);

                return acc;
            },
            {} as Record<string, IResultSucceededSlipSummaryViewModel>,
        );
    }

    private getTitle(): string {
        const pickCount = this.succeededSlips.length;
        const legCount = this.succeededSlips[0].betCount;

        if (this.showOneBetBuilderPerModule || pickCount === 1) {
            return this.isEpcotEnabled
                ? this.messages.linear.BabSGPResultTitle
                : interpolateDoubleBracesString(this.babResultSingleTitle, { babCount: legCount });
        }

        return interpolateDoubleBracesString(this.babResultTitle, { betCount: pickCount });
    }

    private getSubTitle(): string {
        const pickCount = this.succeededSlips.length;

        if (!this.isEpcotEnabled || (!this.showOneBetBuilderPerModule && pickCount !== 1)) {
            return '';
        }

        const legsCount = this.succeededSlips[0].betCount;

        return interpolateDoubleBracesString(this.betbuilderComboHeaderSubTitle, { legsCount });
    }

    private getGroupId(slip: ISucceededSlipResult): string {
        return slip.picks[0].clientPick!.id.id;
    }
}
